import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';

export const ROOT_NODE_ID = 'root';

export const getDirectoryNodeId = (idDocumentDirectory: number): string =>
  `dir-${idDocumentDirectory}`;

export const getDirectoryIdFromNodeId = (nodeId: string): number | null =>
  nodeId === ROOT_NODE_ID ? null : Number(nodeId.replace('dir-', ''));

/**
 * Sortierung der Ordner: zuerst die manuelle Reihenfolge (`Position`), bei
 * Gleichstand alphabetisch. Bestandsordner ohne `Position` bleiben damit
 * alphabetisch, bis sie einmal umsortiert werden.
 */
const byPositionThenTitle = (a: IDocumentDirectory, b: IDocumentDirectory) => {
  const positionDifference = (a.Position ?? 0) - (b.Position ?? 0);

  return positionDifference !== 0
    ? positionDifference
    : a.Title.localeCompare(b.Title, 'de', { sensitivity: 'base' });
};

export const getChildDirectories = (
  documentDirectoryArray: IDocumentDirectory[],
  parent_idDocumentDirectory: number | null
): IDocumentDirectory[] =>
  documentDirectoryArray
    .filter(
      documentDirectory =>
        (documentDirectory.Parent_idDocumentDirectory ?? null) ===
        parent_idDocumentDirectory
    )
    .sort(byPositionThenTitle);

export const getDirectoryById = (
  documentDirectoryArray: IDocumentDirectory[],
  idDocumentDirectory: number | null
): IDocumentDirectory | undefined =>
  idDocumentDirectory === null
    ? undefined
    : documentDirectoryArray.find(
        documentDirectory =>
          documentDirectory.idDocumentDirectory === idDocumentDirectory
      );

/**
 * Pfad von der obersten Ebene bis zum Ordner (einschliesslich). Zyklen —
 * theoretisch durch fehlerhafte Daten moeglich — werden abgefangen.
 */
export const getDirectoryPath = (
  documentDirectoryArray: IDocumentDirectory[],
  idDocumentDirectory: number | null
): IDocumentDirectory[] => {
  const path: IDocumentDirectory[] = [];
  const visited = new Set<number>();
  let current = getDirectoryById(documentDirectoryArray, idDocumentDirectory);

  while (current !== undefined && !visited.has(current.idDocumentDirectory)) {
    visited.add(current.idDocumentDirectory);
    path.unshift(current);
    current = getDirectoryById(
      documentDirectoryArray,
      current.Parent_idDocumentDirectory ?? null
    );
  }

  return path;
};

/** Alle Ordner-IDs unterhalb des Ordners (ohne den Ordner selbst). */
export const getDescendantDirectoryIds = (
  documentDirectoryArray: IDocumentDirectory[],
  idDocumentDirectory: number
): number[] => {
  const result: number[] = [];
  const queue = [idDocumentDirectory];

  while (queue.length > 0) {
    const currentId = queue.shift() as number;

    documentDirectoryArray
      .filter(
        documentDirectory =>
          documentDirectory.Parent_idDocumentDirectory === currentId
      )
      .forEach(documentDirectory => {
        if (!result.includes(documentDirectory.idDocumentDirectory)) {
          result.push(documentDirectory.idDocumentDirectory);
          queue.push(documentDirectory.idDocumentDirectory);
        }
      });
  }

  return result;
};

export const getDocumentsOfDirectory = (
  documentArray: IDocumentDirectoryHasDocument[],
  idDocumentDirectory: number
): IDocumentDirectoryHasDocument[] =>
  documentArray
    .filter(
      documentObject =>
        Number(documentObject.idDocumentDirectory) === idDocumentDirectory
    )
    .sort((a, b) =>
      a.FileName.localeCompare(b.FileName, 'de', { sensitivity: 'base' })
    );

/** Naechste freie temporaere (negative) ID fuer eine Neuanlage. */
export const getNextTempId = (existingIdArray: number[]): number => {
  const minId = existingIdArray.length > 0 ? Math.min(...existingIdArray) : 0;

  return minId >= 0 ? -1 : minId - 1;
};

/**
 * Entfernt einen Ordner mit allen Unterordnern aus den lokalen Arrays.
 *
 * `moveDocumentsTo` bestimmt, was mit den darin abgelegten Dateien passiert:
 * `undefined` loescht sie mit, eine Ordner-ID verschiebt sie dorthin.
 */
export const removeDirectoryRecursive = (
  idDocumentDirectory: number,
  documentDirectoryArray: IDocumentDirectory[],
  documentArray: IDocumentDirectoryHasDocument[],
  moveDocumentsTo?: number
): [IDocumentDirectory[], IDocumentDirectoryHasDocument[]] => {
  const idArray = [
    idDocumentDirectory,
    ...getDescendantDirectoryIds(documentDirectoryArray, idDocumentDirectory),
  ];

  const nextDirectoryArray = documentDirectoryArray.filter(
    documentDirectory =>
      !idArray.includes(documentDirectory.idDocumentDirectory)
  );

  if (moveDocumentsTo === undefined) {
    return [
      nextDirectoryArray,
      documentArray.filter(
        documentObject =>
          !idArray.includes(Number(documentObject.idDocumentDirectory))
      ),
    ];
  }

  return [
    nextDirectoryArray,
    documentArray.map(documentObject =>
      idArray.includes(Number(documentObject.idDocumentDirectory))
        ? { ...documentObject, idDocumentDirectory: moveDocumentsTo }
        : documentObject
    ),
  ];
};

/** Anzahl der Dateien im Ordner einschliesslich aller Unterordner. */
export const countDocumentsInDirectoryTree = (
  idDocumentDirectory: number,
  documentDirectoryArray: IDocumentDirectory[],
  documentArray: IDocumentDirectoryHasDocument[]
): number => {
  const idArray = [
    idDocumentDirectory,
    ...getDescendantDirectoryIds(documentDirectoryArray, idDocumentDirectory),
  ];

  return documentArray.filter(documentObject =>
    idArray.includes(Number(documentObject.idDocumentDirectory))
  ).length;
};

/**
 * Verschiebt einen Ordner innerhalb seiner Ebene vor bzw. hinter einen anderen
 * und nummeriert die Geschwister neu durch.
 *
 * `placeAfter` entscheidet, ob der Ordner hinter dem Zielordner einsortiert wird
 * (Drop auf der unteren Haelfte) oder davor.
 */
export const reorderDirectory = (
  idDocumentDirectory: number,
  idTargetDocumentDirectory: number,
  placeAfter: boolean,
  documentDirectoryArray: IDocumentDirectory[]
): IDocumentDirectory[] => {
  const moved = getDirectoryById(documentDirectoryArray, idDocumentDirectory);
  const target = getDirectoryById(
    documentDirectoryArray,
    idTargetDocumentDirectory
  );

  if (
    moved === undefined ||
    target === undefined ||
    (moved.Parent_idDocumentDirectory ?? null) !==
      (target.Parent_idDocumentDirectory ?? null)
  ) {
    return documentDirectoryArray;
  }

  const siblingArray = getChildDirectories(
    documentDirectoryArray,
    moved.Parent_idDocumentDirectory ?? null
  ).filter(entry => entry.idDocumentDirectory !== idDocumentDirectory);

  const targetIndex = siblingArray.findIndex(
    entry => entry.idDocumentDirectory === idTargetDocumentDirectory
  );

  if (targetIndex < 0) {
    return documentDirectoryArray;
  }

  siblingArray.splice(placeAfter ? targetIndex + 1 : targetIndex, 0, moved);

  const positionByIdMap = new Map(
    siblingArray.map((entry, index) => [entry.idDocumentDirectory, index])
  );

  return documentDirectoryArray.map(documentDirectory =>
    positionByIdMap.has(documentDirectory.idDocumentDirectory)
      ? {
          ...documentDirectory,
          Position: positionByIdMap.get(
            documentDirectory.idDocumentDirectory
          ) as number,
        }
      : documentDirectory
  );
};
